/* OkaRojuPlayer — "Oka Roju — Live One Day In This Life".
   Cinematic one-screen-at-a-time story player. Dark serif theme.
   All story text rendered verbatim; tags never shown; no right/wrong feedback.
   Access is enforced by the backend (same locks as the preview). */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { cardToast } from '../../lib/clarityCard';
import { Link } from 'react-router-dom';
import { X, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PARCHMENT = '#F5E9D4';
const DEEP = '#0B1F2E';
const PANEL = '#1A3548';
const SERIF = '"Fraunces", "Cormorant Garamond", "Georgia", serif';

const Pill = ({ children, onClick, testId, subtle = false }) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full px-8 font-semibold uppercase tracking-[0.14em] transition-transform hover:scale-[1.02]"
    style={{
      background: subtle ? 'transparent' : PARCHMENT,
      color: subtle ? PARCHMENT : DEEP,
      border: subtle ? `1px solid ${PARCHMENT}66` : 'none',
      minHeight: 48, fontSize: 12,
    }}
    data-testid={testId}
  >{children}</button>
);

export const OkaRojuPlayer = ({ pathKey, itemId, onClose }) => {
  const [story, setStory] = useState(null);
  const [err, setErr] = useState('');
  const [stage, setStage] = useState('start');   // start | screen | dayend | final | done
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [tags, setTags] = useState([]);
  const [finalPick, setFinalPick] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/okaroju/${pathKey}/${itemId}`)
      .then((r) => setStory(r.data))
      .catch((e) => {
        const code = e?.response?.status;
        setErr(code === 423 || code === 403
          ? 'This story unlocks together with its preview.'
          : 'This story is being added.');
      });
  }, [pathKey, itemId]);

  const choose = (c, i) => {
    if (picked !== null) return;
    setPicked(i);
    setTags((t) => [...t, ...(c.tags || [])]);
  };

  const next = () => {
    setPicked(null);
    if (idx < 3) setIdx(idx + 1);
    else setStage('dayend');
  };

  const answerFinal = (opt) => {
    if (finalPick !== null) return;
    setFinalPick(opt);
    axios.post(`${API}/api/okaroju/answer`, {
      path_key: pathKey, story_id: story.story_id, answer: opt, tags,
    }).then(() => cardToast()).catch(() => {});
  };

  const screen = story?.screens?.[idx];
  const dotOn = stage === 'start' ? -1 : stage === 'screen' ? idx : 4;

  return (
    <div
      className="fixed inset-0 z-[95] flex flex-col"
      style={{ background: DEEP, fontFamily: '"Inter", system-ui, sans-serif' }}
      data-testid="okaroju-player"
    >
      {/* Top bar: 5 progress dots + quiet Exit (saves nothing) */}
      <div className="flex items-center justify-between px-4 pt-4 shrink-0">
        <span className="w-[64px]" aria-hidden />
        <div className="flex items-center gap-2.5" data-testid="okaroju-dots">
          {[0, 1, 2, 3, 4].map((d) => (
            <span key={d} className="rounded-full transition-all duration-500"
              style={{
                width: d <= dotOn ? 22 : 8, height: 8,
                background: d <= dotOn ? PARCHMENT : `${PARCHMENT}33`,
              }} />
          ))}
        </div>
        <button type="button" onClick={onClose}
          className="inline-flex items-center gap-1 rounded-full px-4 text-xs uppercase tracking-widest"
          style={{ color: `${PARCHMENT}88`, minHeight: 44 }}
          data-testid="okaroju-exit">
          Exit <X size={14} />
        </button>
      </div>

      {/* One screen per viewport */}
      <div className="flex-1 flex items-center justify-center px-5 overflow-hidden">
        <div className="w-full max-w-xl">
          {err && (
            <div className="text-center" data-testid="okaroju-error">
              <p style={{ color: PARCHMENT, fontSize: 15 }}>{err}</p>
              <div className="mt-6"><Pill onClick={onClose} testId="okaroju-error-close">Close</Pill></div>
            </div>
          )}

          {!err && !story && (
            <p className="text-center text-sm" style={{ color: `${PARCHMENT}88` }}>Setting the scene…</p>
          )}

          {story && stage === 'start' && (
            <div className="text-center" data-testid="okaroju-start">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] mb-4" style={{ color: `${PARCHMENT}77` }}>
                Oka Roju — Live One Day In This Life
              </p>
              <h1 className="italic mb-4" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(26px,5vw,40px)', lineHeight: 1.2 }}>
                {story.title}
              </h1>
              <p className="mb-8" style={{ color: `${PARCHMENT}AA`, fontSize: 14 }}>
                Live one day in this life — 90 seconds
              </p>
              <Pill onClick={() => { setStage('screen'); setIdx(0); }} testId="okaroju-start-btn">Start My Day</Pill>
              {story.illustrative_note && (
                <p className="mt-6 text-xs" style={{ color: `${PARCHMENT}77` }} data-testid="okaroju-illustrative-note">{story.illustrative_note}</p>
              )}
            </div>
          )}

          {story && stage === 'screen' && screen && (
            <div data-testid={`okaroju-screen-${idx + 1}`}>
              <div className="italic mb-3" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(34px,6vw,52px)', lineHeight: 1 }}>
                {screen.time}
              </div>
              <p className="mb-6 leading-relaxed" style={{ color: `${PARCHMENT}DD`, fontSize: 'clamp(14px,1.8vw,16px)' }}>
                {screen.situation}
              </p>
              <div className="space-y-3">
                {screen.choices.map((c, i) => (
                  <button
                    key={i} type="button" onClick={() => choose(c, i)}
                    className="w-full text-left rounded-2xl px-5 py-4 transition-all"
                    style={{
                      minHeight: 56,
                      background: picked === i ? `${PARCHMENT}1F` : PANEL,
                      border: picked === i ? `2px solid ${PARCHMENT}` : `1px solid ${PARCHMENT}44`,
                      color: PARCHMENT, fontSize: 14.5, lineHeight: 1.5,
                      opacity: picked !== null && picked !== i ? 0.35 : 1,
                    }}
                    data-testid={`okaroju-choice-${i}`}
                  >{c.text}</button>
                ))}
              </div>
              {picked !== null && (
                <div className="mt-5" data-testid="okaroju-consequence">
                  <div className="rounded-2xl px-5 py-4"
                    style={{ background: `${PARCHMENT}14`, borderLeft: `3px solid ${PARCHMENT}` }}>
                    <p className="leading-relaxed" style={{ color: PARCHMENT, fontSize: 14.5 }}>
                      {screen.choices[picked].consequence}
                    </p>
                  </div>
                  <div className="text-center mt-5">
                    <Pill onClick={next} testId="okaroju-continue">Continue</Pill>
                  </div>
                </div>
              )}
            </div>
          )}

          {story && stage === 'dayend' && (
            <div data-testid="okaroju-dayend">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] mb-3" style={{ color: `${PARCHMENT}77` }}>
                Day Ends
              </p>
              <p className="italic mb-5 leading-snug" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(18px,2.6vw,24px)' }}>
                {story.day_end.summary}
              </p>
              <div className="rounded-2xl px-5 py-4 mb-4" style={{ background: PANEL, border: `1px solid ${PARCHMENT}33` }}>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: `${PARCHMENT}77` }}>AI Reality</p>
                <p className="leading-relaxed" style={{ color: `${PARCHMENT}CC`, fontSize: 13.5 }}>{story.day_end.ai_reality}</p>
                <a href="/ai-disclaimer" target="_blank" rel="noreferrer"
                  className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest underline underline-offset-2"
                  style={{ color: `${PARCHMENT}66` }}
                  data-testid="okaroju-ai-disclaimer-link">
                  About our AI content
                </a>
              </div>
              <div className="rounded-2xl px-5 py-4 mb-6" style={{ background: `${PARCHMENT}10`, border: `1px dashed ${PARCHMENT}55` }}>
                <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: `${PARCHMENT}88` }}>
                  <Sparkles size={11} /> Life skill for this life
                </p>
                <p className="leading-relaxed" style={{ color: PARCHMENT, fontSize: 13.5 }}>{story.day_end.life_skill_nudge}</p>
                <Link to="/life-skills" className="inline-block mt-2 text-xs font-bold uppercase tracking-widest underline underline-offset-4"
                  style={{ color: PARCHMENT }} data-testid="okaroju-lifeskills-link">
                  Explore Life Skills
                </Link>
              </div>
              <div className="text-center">
                <Pill onClick={() => setStage('final')} testId="okaroju-to-final">Continue</Pill>
              </div>
            </div>
          )}

          {story && stage === 'final' && (
            <div data-testid="okaroju-final">
              <p className="italic mb-6 leading-snug" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(20px,3vw,28px)' }}>
                {story.final_question.text}
              </p>
              <div className="space-y-3">
                {story.final_question.options.map((opt, i) => (
                  <button
                    key={i} type="button" onClick={() => answerFinal(opt)}
                    className="w-full text-left rounded-2xl px-5 py-4 transition-all"
                    style={{
                      minHeight: 52,
                      background: finalPick === opt ? `${PARCHMENT}1F` : PANEL,
                      border: finalPick === opt ? `2px solid ${PARCHMENT}` : `1px solid ${PARCHMENT}44`,
                      color: PARCHMENT, fontSize: 14.5,
                      opacity: finalPick !== null && finalPick !== opt ? 0.35 : 1,
                    }}
                    data-testid={`okaroju-final-${i}`}
                  >{opt}</button>
                ))}
              </div>
              {finalPick !== null && (
                <div className="mt-6 text-center" data-testid="okaroju-after-answer">
                  {/maybe/i.test(finalPick) && story.final_question.related && (
                    <p className="mb-5 text-sm" style={{ color: `${PARCHMENT}CC` }}>
                      Related path you might like:{' '}
                      <Link to={pathKey === 'specializations' ? '/specializations' : pathKey === 'job_roles' ? '/job-roles' : '/ai-pathway'}
                        className="font-bold underline underline-offset-4" style={{ color: PARCHMENT }}
                        data-testid="okaroju-related-link">
                        {story.final_question.related}
                      </Link>
                    </p>
                  )}
                  <Pill onClick={onClose} testId="okaroju-done">Back to the preview</Pill>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* CTA banner placed at the bottom of preview pages. */
export const LiveOneDayButton = ({ pathKey, itemId, roleName }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full mt-8 rounded-3xl px-6 py-6 text-left transition-transform hover:scale-[1.01]"
        style={{ background: DEEP, minHeight: 44 }}
        data-testid="live-one-day-btn"
      >
        <span className="block text-[10px] font-bold uppercase tracking-[0.3em] mb-1.5" style={{ color: `${PARCHMENT}77` }}>
          Oka Roju — Live One Day In This Life
        </span>
        <span className="flex items-center justify-between gap-3">
          <span className="italic" style={{ color: PARCHMENT, fontFamily: SERIF, fontSize: 'clamp(18px,2.4vw,24px)' }}>
            Live one day as {roleName ? `a ${roleName}` : 'this role'} — 90 seconds
          </span>
          <span className="rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-widest shrink-0"
            style={{ background: PARCHMENT, color: DEEP }}>
            Start My Day
          </span>
        </span>
      </button>
      {open && <OkaRojuPlayer pathKey={pathKey} itemId={String(itemId)} onClose={() => setOpen(false)} />}
    </>
  );
};

export default OkaRojuPlayer;
